import { readFileSync, readdirSync, existsSync } from "node:fs";
import { resolve, basename } from "node:path";
import yaml from "js-yaml";
import {
  GuardrailTemplateSchema,
  type GuardrailTemplate,
  type IntentConfig,
  type ResolvedGuardrails,
  type TemplateInfo,
} from "./schema.js";

function findDataDir(): string {
  const base = import.meta.dirname ?? __dirname;
  // Try bundled location first (dist/data/ when installed from npm)
  const bundled = resolve(base, "data", "guardrails");
  try {
    readdirSync(bundled);
    return bundled;
  } catch {
    // Fall back to monorepo location (packages/intent/data/guardrails from dist/)
    return resolve(base, "..", "data", "guardrails");
  }
}

/**
 * Load a guardrail template by name.
 */
export function loadTemplate(name: string): GuardrailTemplate {
  // SEC-02: Prevent path traversal via template name
  if (!/^[a-zA-Z0-9_-]+$/.test(name)) {
    throw new Error(
      `Invalid template name: "${name}". Template names must contain only letters, digits, hyphens, and underscores.`,
    );
  }

  const dir = findDataDir();
  const filePath = resolve(dir, `${name}.yaml`);

  let content: string;
  try {
    content = readFileSync(filePath, "utf-8");
  } catch {
    throw new Error(
      `Unknown guardrail template: "${name}". Use listTemplates() to see available templates.`,
    );
  }

  const raw = yaml.load(content, { schema: yaml.JSON_SCHEMA });
  return GuardrailTemplateSchema.parse(raw);
}

/**
 * List all available guardrail templates.
 */
export function listTemplates(): TemplateInfo[] {
  const dir = findDataDir();
  const files = readdirSync(dir).filter((f) => f.endsWith(".yaml"));

  return files.map((file) => {
    const content = readFileSync(resolve(dir, file), "utf-8");
    const raw = yaml.load(content, { schema: yaml.JSON_SCHEMA }) as Record<string, unknown>;
    return {
      name: raw.name as string,
      description: raw.description as string,
      category: raw.category as string,
      status: raw.status as string | undefined,
    };
  });
}

/**
 * Load community guardrail packs from {projectDir}/.fathom/guardrails/ directory.
 * Each .yaml file must match GuardrailTemplateSchema. Invalid files are skipped with a warning.
 * Returns empty array if directory doesn't exist or is empty.
 */
export function loadCommunityPacks(projectDir: string): GuardrailTemplate[] {
  const guardrailsDir = resolve(projectDir, ".fathom", "guardrails");

  if (!existsSync(guardrailsDir)) {
    return [];
  }

  let files: string[];
  try {
    files = readdirSync(guardrailsDir).filter((f) => f.endsWith(".yaml"));
  } catch {
    return [];
  }

  const packs: GuardrailTemplate[] = [];

  for (const file of files) {
    try {
      const content = readFileSync(resolve(guardrailsDir, file), "utf-8");
      const raw = yaml.load(content, { schema: yaml.JSON_SCHEMA });
      const result = GuardrailTemplateSchema.safeParse(raw);
      if (result.success) {
        packs.push(result.data);
      } else {
        process.stderr.write(`Warning: Skipping invalid guardrail pack "${file}": ${result.error.message}\n`);
      }
    } catch {
      process.stderr.write(`Warning: Skipping unreadable guardrail pack "${file}"\n`);
    }
  }

  return packs;
}

/**
 * Resolve all guardrail templates and custom rules from an intent config
 * into a flat categorized object. If projectDir is provided, also loads
 * community packs from {projectDir}/.fathom/guardrails/.
 */
export function resolveGuardrails(intent: IntentConfig, projectDir?: string): ResolvedGuardrails {
  const result: ResolvedGuardrails = {
    security: [],
    quality: [],
    process: [],
  };

  if (intent.guardrails) {
    const { guardrails } = intent;

    // Expand security templates
    if (guardrails.security) {
      for (const templateName of guardrails.security.templates) {
        const template = loadTemplate(templateName);
        if (template.category === "security") {
          result.security.push(...template.rules);
        } else if (template.category === "quality") {
          result.quality.push(...template.rules);
        }
      }

      // Add custom security rules
      result.security.push(...guardrails.security.custom);
    }

    // Add quality rules (these are direct string rules, not template refs)
    result.quality.push(...guardrails.quality);

    // Add process rules
    result.process.push(...guardrails.process);
  }

  // Merge community packs if projectDir provided (independent of intent.guardrails)
  if (projectDir) {
    const communityPacks = loadCommunityPacks(projectDir);
    for (const pack of communityPacks) {
      if (pack.category === "security") {
        result.security.push(...pack.rules);
      } else if (pack.category === "quality") {
        result.quality.push(...pack.rules);
      }
    }
  }

  return result;
}
